import React, { useState, useEffect, useRef } from 'react'
import { TruckIcon, MapIcon } from '@heroicons/react/20/solid'

import LoadChart from './LoadChart'
import LoadDetails from './LoadDetails'
import MapModal from '../../Maps/MapModal'

import { getCurrentUser } from '../../../services/authService'

import './loadcollapse.css'

function LoadCollapse({
	load,
	isDarkMode,
	styles,
	isSimilarLoads,
	onPostTruck,
	onFacilityClick,
}) {
	const [isMapModalOpen, setMapModalOpen] = useState(false)
	const [isLoadLineMapOpen, setLoadLineMapOpen] = useState(false)
	const [activeTab, setActiveTab] = useState('details')
	const [indicatorStyle, setIndicatorStyle] = useState({})
	const tabRefs = useRef({})
	const containerRef = useRef(null)

	const user = getCurrentUser()
	const isAdmin = user?.isAdmin || false
	const showTabs = load?.stops && load.stops.length > 0

	const viewOnMapClick = event => {
		event.stopPropagation()
		setMapModalOpen(true)
	}

	const closeMapModal = () => {
		setMapModalOpen(false)
	}

	const handlePostTruckClick = event => {
		event.stopPropagation()
		onPostTruck?.(load)
	}

	const generateGoogleMapsUrl = load => {
		let allStops = []

		// Handle the actual load data structure where load.stops contains the stop groups
		if (load.stops && load.stops.length > 0) {
			load.stops.forEach((stopGroup, index) => {
				const stops = stopGroup.stops

				if (stops && stops.length > 0) {
					if (index === 0) {
						allStops.push(stops[0])
					}

					for (let i = 1; i < stops.length; i++) {
						const lastAddedStop = allStops[allStops.length - 1]
						if (lastAddedStop.location.label !== stops[i].location.label) {
							allStops.push(stops[i])
						}
					}
				}
			})

			// Add the last stop from the last stop group
			const lastStopGroup = load.stops[load.stops.length - 1]
			if (
				lastStopGroup &&
				lastStopGroup.stops &&
				lastStopGroup.stops.length > 0
			) {
				const lastStop = lastStopGroup.stops[lastStopGroup.stops.length - 1]
				if (
					allStops.length === 0 ||
					allStops[allStops.length - 1].location.label !==
						lastStop.location.label
				) {
					allStops.push(lastStop)
				}
			}
		} else if (load.pickup && load.dropOff) {
			// Fallback to simple pickup/dropoff structure
			const pickupStop = {
				location: {
					label: `${load.pickup.address.city}, ${load.pickup.address.state}`,
					city: load.pickup.address.city,
					state: load.pickup.address.state,
					latitude: load.pickup.address.latitude,
					longitude: load.pickup.address.longitude,
				},
			}
			const dropoffStop = {
				location: {
					label: `${load.dropOff.address.city}, ${load.dropOff.address.state}`,
					city: load.dropOff.address.city,
					state: load.dropOff.address.state,
					latitude: load.dropOff.address.latitude,
					longitude: load.dropOff.address.longitude,
				},
			}
			allStops = [pickupStop, dropoffStop]
		}

		if (allStops.length === 0) {
			throw new Error('No stops found in load data')
		}

		const locations = allStops.map(stop => {
			const location = stop.location
			if (location.latitude && location.longitude) {
				return `${location.latitude},${location.longitude}`
			} else {
				return `${location.city},${location.state}`.replace(/\s+/g, '+')
			}
		})

		let url
		if (locations.length === 1) {
			url = `https://www.google.com/maps/search/?api=1&query=${locations[0]}`
		} else if (locations.length === 2) {
			url = `https://www.google.com/maps/dir/?api=1&origin=${locations[0]}&destination=${locations[1]}&travelmode=driving`
		} else {
			const origin = locations[0]
			const destination = locations[locations.length - 1]
			const waypoints = locations.slice(1, -1).join('|')
			url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`
		}

		return encodeURI(url)
	}

	const handleMapsClick = event => {
		event.stopPropagation()

		// Check localStorage for built-in map preference (default: true)
		const savedPreference = localStorage.getItem('loadfetcher-use-embedded-map')
		const useBuiltInMap = savedPreference === null || JSON.parse(savedPreference) === true

		if (useBuiltInMap) {
			// Open built-in map modal
			setLoadLineMapOpen(true)
		} else {
			// Open Google Maps directly in new tab
			const mapUrl = generateGoogleMapsUrl(load)
			window.open(mapUrl, '_blank')
		}
	}

	const closeLoadLineMap = () => {
		setLoadLineMapOpen(false)
	}

	// Handle tab indicator animation
	useEffect(() => {
		const updateIndicator = () => {
			const activeButton = tabRefs.current[activeTab]
			const container = containerRef.current

			if (activeButton && container) {
				const containerRect = container.getBoundingClientRect()
				const buttonRect = activeButton.getBoundingClientRect()

				setIndicatorStyle({
					left: buttonRect.left - containerRect.left,
					width: buttonRect.width,
					height: buttonRect.height,
				})
			}
		}

		updateIndicator()
		window.addEventListener('resize', updateIndicator)

		return () => window.removeEventListener('resize', updateIndicator)
	}, [activeTab])

	return (
		<div
			className="w-full col-span-12 transition-all px-2 pb-2 md:py-3"
			style={{ ...styles, height: 'auto', overflow: 'hidden' }}
		>
			{/* Tabs for test users with stops data */}
			{showTabs && (
				<div className="w-full mt-1.5 mb-1 md:mt-0">
					<div className="flex justify-between items-center">
						<div
							className="p-0.5 rounded-md flex relative"
							style={{
								backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
							}}
							ref={containerRef}
						>
							{/* Sliding indicator */}
							<div
								className="absolute rounded-md shadow-sm transition-all duration-300 ease-out"
								style={{
									left: indicatorStyle.left || 0,
									width: indicatorStyle.width || 0,
									height: indicatorStyle.height || 0,
									top: '2px',
									backgroundColor: isDarkMode ? '#22272e' : '#ffffff',
								}}
							/>
							<button
								ref={el => (tabRefs.current['details'] = el)}
								onClick={e => {
									e.stopPropagation()
									setActiveTab('details')
								}}
								className={`relative z-10 px-4 py-1 rounded-md text-xs font-medium transition-all duration-300 ${
									activeTab === 'details'
										? isDarkMode
											? 'text-gray-100'
											: 'text-gray-900'
										: isDarkMode
										? 'text-gray-400 hover:text-gray-200'
										: 'text-gray-600 hover:text-gray-900'
								}`}
							>
								Details
							</button>
							<button
								ref={el => (tabRefs.current['chart'] = el)}
								onClick={e => {
									e.stopPropagation()
									setActiveTab('chart')
								}}
								className={`relative z-10 px-4 py-1 rounded-md text-xs font-medium transition-all duration-300 ${
									activeTab === 'chart'
										? isDarkMode
											? 'text-gray-100'
											: 'text-gray-900'
										: isDarkMode
										? 'text-gray-400 hover:text-gray-200'
										: 'text-gray-600 hover:text-gray-900'
								}`}
							>
								Chart
							</button>
						</div>

						{/* Toll Charge */}
						{load?.tollCharge && (
							<div className="text-sm pr-4">
								<span
									className={`${
										isDarkMode ? 'text-gray-400' : 'text-gray-600'
									} mr-1`}
								>
									Toll:
								</span>
								<span
									className={`font-medium ${
										isDarkMode ? 'text-gray-100' : 'text-gray-900'
									}`}
								>
									${load.tollCharge.toFixed(2)}
								</span>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Conditional rendering based on tabs */}
			{showTabs && activeTab === 'details' ? (
				<LoadDetails load={load} isDarkMode={isDarkMode} styles={styles} onFacilityClick={onFacilityClick} />
			) : (
				<LoadChart
					load={load}
					isDarkMode={isDarkMode}
					styles={styles}
					isSimilarLoads={isSimilarLoads}
					isMapModalOpen={isMapModalOpen}
					closeMapModal={closeMapModal}
					viewOnMapClick={viewOnMapClick}
					isAdmin={isAdmin}
				/>
			)}

			{/* LoadLine Map Modal */}
			{isLoadLineMapOpen && (
				<MapModal
					mapUrl={generateGoogleMapsUrl(load)}
					load={load}
					onClose={closeLoadLineMap}
					startingLocation={null}
				/>
			)}

			{/* Post a Truck and Maps buttons - hidden on mobile */}
			<div className="pt-1 mt-0 hidden md:block">
				<div className="flex justify-start gap-2">
					<button
						className={`flex items-center px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
							isDarkMode
								? 'bg-gray-700 hover:bg-gray-600 text-gray-200 border-gray-600'
								: 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
						}`}
						onClick={handlePostTruckClick}
					>
						<TruckIcon className="h-4 w-4 mr-1" aria-hidden="true" />
						Post A Truck
					</button>
					<button
						className={`flex items-center px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
							isDarkMode
								? 'bg-gray-700 hover:bg-gray-600 text-gray-200 border-gray-600'
								: 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
						}`}
						onClick={handleMapsClick}
					>
						<MapIcon className="h-4 w-4 mr-1" aria-hidden="true" />
						Maps
					</button>
				</div>
			</div>
		</div>
	)
}

export default LoadCollapse
